import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { ROUTE_CONFIG } from "@/routes/config";
import { ProviderId, ProviderLogo } from "@/components/ProviderLogo/ProviderLogo";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { ApiKeyHubspot } from "@/pages/Private/pages/Company/components/ApiKeyHubspot";
import { ApiKeyLemlist } from "@/pages/Private/pages/Company/components/ApiKeyLemlist";
import { ZohoSettings } from "@/pages/Private/pages/Company/components/ZohoSettings";
import { PipedriveSettings } from "@/pages/Private/pages/Company/components/PipedriveSettings";
import { DynamicsSettings } from "@/pages/Private/pages/Company/components/DynamicsSettings";
import { SalesforceSettings } from "@/pages/Private/pages/Company/components/SalesforceSettings";
import { CompanyType } from "@/pages/Private/pages/Company/schema/company";
import { WebhookOverview } from "@/pages/Private/pages/Company/pages/WebhookOverview/WebhookOverview";

import { CATALOG } from "../constants/catalog";
import { McpServerGuide } from "./McpServerGuide/McpServerGuide";
import { N8nGuide } from "./N8nGuide/N8nGuide";
import { IntegrationApiKeys } from "./IntegrationApiKeys";

interface IntegrationDetailProps {
	provider: ProviderId;
}

export const IntegrationDetail: FunctionComponent<IntegrationDetailProps> = ({ provider }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();

	const entry = CATALOG.find((c) => c.id === provider);
	const company = user?.company as CompanyType | undefined;

	// Guard permission-gated management pages (API keys / webhooks) against direct URL access.
	if (entry?.permission && !hasPermissions([entry.permission])) {
		return <Navigate replace to={ROUTE_CONFIG.INTEGRATIONS} />;
	}

	const renderProvider = () => {
		switch (provider) {
			case "hubspot":
				return <ApiKeyHubspot company={company} />;
			case "zoho":
				return <ZohoSettings />;
			case "pipedrive":
				return <PipedriveSettings />;
			case "msdynamics":
				return <DynamicsSettings />;
			case "salesforce":
				return <SalesforceSettings />;
			case "lemlist":
				return <ApiKeyLemlist company={company} />;
			case "mcp":
				return <McpServerGuide />;
			case "n8n":
				return <N8nGuide />;
			case "api":
				return <IntegrationApiKeys />;
			case "webhooks":
				return <WebhookOverview />;
			default:
				return null;
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<button
				className="flex items-center gap-2 text-sm text-text-meta hover:text-text w-fit"
				type="button"
				onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS)}
			>
				<Icon icon={faArrowLeft} />
				<span>{t("integrations.detail.back")}</span>
			</button>

			<div className="flex items-center gap-3">
				<ProviderLogo provider={provider} size={36} />
				<div>
					<h1 className="text-text text-xl font-semibold">
						{entry ? t(entry.titleKey) : provider}
					</h1>
					{entry && <p className="text-text-meta text-sm">{t(entry.descriptionKey)}</p>}
				</div>
			</div>

			<div>{renderProvider()}</div>
		</div>
	);
};
